import { cn } from '@/lib/utils';
import type { DataQuality } from '@/lib/types';

const META: Record<DataQuality, { label: string; dot: string; text: string }> = {
  fresh: { label: 'Fresh', dot: 'bg-primary', text: 'text-primary' },
  stale: { label: 'Stale', dot: 'bg-yellow-400', text: 'text-yellow-400' },
  unavailable: { label: 'Unavailable', dot: 'bg-red-500', text: 'text-red-400' },
};

export function DataStatus({
  quality,
  showLabel = true,
  className,
}: {
  quality: DataQuality;
  showLabel?: boolean;
  className?: string;
}) {
  const m = META[quality];
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs', className)}>
      <span className={cn('h-2 w-2 rounded-full', m.dot)} />
      {showLabel && <span className={m.text}>{m.label}</span>}
    </span>
  );
}

export function timeAgo(ts: number): string {
  if (!ts) return 'never';
  // Clamp to 0: a source/server timestamp slightly ahead of the local clock
  // must never render as "-3s ago".
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

type FeedHealthLevel = 'healthy' | 'degraded' | 'issue';

/**
 * Derive an honest, human-friendly feed-health status. This never shows a raw
 * "0/24" style ratio; instead it distinguishes healthy / degraded / serious
 * states based on how many indicators are available and how fresh the data is.
 */
export function computeFeedHealth({
  available,
  total,
  lastUpdated,
}: {
  available: number;
  total: number;
  lastUpdated: number;
}): { level: FeedHealthLevel; dot: string; text: string; label: string } {
  const ageMs = lastUpdated ? Date.now() - lastUpdated : Infinity;
  const veryStale = ageMs > 3 * 60 * 60 * 1000; // > 3h with no update
  const updated = lastUpdated ? `Updated ${timeAgo(lastUpdated)}` : 'No recent update';

  // Serious problem: nothing available, or data hasn't updated in hours.
  if (available === 0 || veryStale) {
    return {
      level: 'issue',
      dot: 'bg-red-500',
      text: 'text-red-400',
      label:
        available === 0
          ? `Data issue detected · ${updated}`
          : `Data may be delayed · Last update ${timeAgo(lastUpdated)}`,
    };
  }

  // Fully healthy.
  if (available >= total) {
    return {
      level: 'healthy',
      dot: 'bg-primary',
      text: 'text-primary',
      label: `${total} indicators monitored · ${updated}`,
    };
  }

  // Partial coverage.
  return {
    level: 'degraded',
    dot: 'bg-yellow-400',
    text: 'text-yellow-300',
    label: `${available}/${total} indicators available · ${updated}`,
  };
}

/**
 * Compact, colour-coded feed-health pill for the dashboard header.
 */
export function FeedHealth({
  available,
  total,
  lastUpdated,
  className,
}: {
  available: number;
  total: number;
  lastUpdated: number;
  className?: string;
}) {
  const h = computeFeedHealth({ available, total, lastUpdated });
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-xs font-medium',
        h.text,
        className,
      )}
    >
      <span className={cn('h-2 w-2 rounded-full', h.dot)} />
      {h.label}
    </span>
  );
}
